import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Play, X } from 'lucide-react';
import { useRewardedVideo } from '@/hooks/useRewardedVideo';
import { useLanguage } from '@/contexts/LanguageContext';

interface RewardedVideoPlayerProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const RewardedVideoPlayer: React.FC<RewardedVideoPlayerProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const { watchRewardedVideo, isWatching } = useRewardedVideo();
  const { language } = useLanguage();
  const [progress, setProgress] = useState(0);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (open && !isWatching) {
      setProgress(0);
      setCanClose(false);
    }
  }, [open, isWatching]);

  useEffect(() => {
    if (isWatching) {
      // Simulate video progress (5 seconds)
      const interval = setInterval(() => {
        setProgress(prev => {
          const next = prev + 2; // 2% every 100ms = 5 seconds total
          if (next >= 100) {
            setCanClose(true);
            clearInterval(interval);
            return 100;
          }
          return next;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isWatching]);

  const handleWatch = async () => {
    const success = await watchRewardedVideo();
    if (success) {
      setTimeout(() => {
        onComplete?.();
        onClose();
      }, 1000);
    }
  };

  const handleClose = () => {
    if (canClose || !isWatching) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {language === 'es' ? 'Video Recompensado' : 'Rewarded Video'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!isWatching ? (
            <>
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {language === 'es'
                    ? 'Mira un video corto para ganar recompensas'
                    : 'Watch a short video to earn rewards'}
                </p>
              </div>

              <div className="p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
                <p className="font-medium text-center">
                  +10 XP • +5 {language === 'es' ? 'puntos' : 'points'}
                </p>
              </div>

              <Button onClick={handleWatch} className="w-full" size="lg">
                <Play className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Ver Video' : 'Watch Video'}
              </Button>

              <Button onClick={onClose} variant="ghost" className="w-full">
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </>
          ) : (
            <>
              <div className="text-center space-y-4">
                {/* Simulated video player */}
                <div className="w-full aspect-video bg-black rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 animate-pulse" />
                  <Play className="w-16 h-16 text-white/80 relative z-10" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {language === 'es' ? 'Progreso' : 'Progress'}
                    </span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                {canClose ? (
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                    {language === 'es' ? '¡Video completado!' : 'Video completed!'}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' 
                      ? 'Por favor espera a que termine el video...'
                      : 'Please wait for the video to finish...'}
                  </p>
                )}
              </div>

              {canClose && (
                <Button onClick={handleClose} className="w-full">
                  <X className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
